import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Spin } from "common/elements";
import { SessionDivider } from "features/user/userMessage/chat/SessionDivider";
import { HistorySession } from "../api";
import { Message, MessageRole } from "../types";
import { MessageBubble } from "./MessageBubble";
import { TypingIndicator } from "./TypingIndicator";
import { ScrollToBottomButton } from "./ScrollToBottomButton";

interface MessageAreaProps {
  messages: Message[];
  isTyping: boolean;
  currentSessionId: string | null;
  userIcon?: string;
  historySessions?: HistorySession[];
  isLoadingHistory?: boolean;
  isFetchingNextPage?: boolean;
  onScrollTop?: () => void;
}

export const MessageArea = ({
  messages,
  isTyping,
  currentSessionId,
  userIcon,
  historySessions = [],
  isLoadingHistory = false,
  isFetchingNextPage = false,
  onScrollTop,
}: MessageAreaProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastUserMsgRef = useRef<HTMLDivElement>(null);
  const lastMsgRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [lastMsgMinHeight, setLastMsgMinHeight] = useState(0);

  const checkScrollPosition = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);

    // 上端スクロール検知でページング
    if (scrollTop === 0 && onScrollTop) {
      onScrollTop();
    }
  }, [onScrollTop]);

  // メッセージ追加の検知
  const prevMessagesLenRef = useRef(messages.length);
  const pendingScrollRef = useRef(false);

  useEffect(() => {
    if (messages.length > prevMessagesLenRef.current) {
      const newMessage = messages[messages.length - 1];

      // ユーザーメッセージ追加時: サイズを記録、次のAIメッセージ追加を待つ
      if (newMessage?.role === MessageRole.USER) {
        pendingScrollRef.current = true;
      }

      // AIメッセージ追加時: minHeight設定 + scrollIntoView
      if (newMessage?.role === MessageRole.ASSISTANT && pendingScrollRef.current) {
        pendingScrollRef.current = false;
        if (scrollRef.current && lastUserMsgRef.current) {
          const containerH = scrollRef.current.clientHeight;
          const userMsgH = lastUserMsgRef.current.clientHeight;
          const PADDING_TOP = 24 * 2; // p-6
          const GAP = 20 * 2; // gap-5
          setLastMsgMinHeight(Math.max(0, containerH - userMsgH - PADDING_TOP - GAP));

          requestAnimationFrame(() => {
            if (scrollRef.current && lastUserMsgRef.current) {
              scrollRef.current.scrollTo({
                top: lastUserMsgRef.current.offsetTop,
                behavior: "smooth",
              });
            }
            setShowScrollButton(false);
          });
        }
      }
    }
    prevMessagesLenRef.current = messages.length;
  }, [messages]);

  // セッション変更時のリセット
  const prevSessionIdRef = useRef(currentSessionId);
  const needsScrollToBottomRef = useRef(false);

  useEffect(() => {
    if (currentSessionId !== prevSessionIdRef.current) {
      setLastMsgMinHeight(0);
      setShowScrollButton(false);
      pendingScrollRef.current = false;

      if (currentSessionId === null) {
        // 新しい会話開始: 履歴の末尾（最下部）へスクロール
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        });
      } else {
        needsScrollToBottomRef.current = true;
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      }
    }
    prevSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  // messagesが変わった時、needsScrollToBottomフラグが立っていたらscrollToBottom
  useEffect(() => {
    if (needsScrollToBottomRef.current && messages.length > 0) {
      needsScrollToBottomRef.current = false;
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          setShowScrollButton(false);
        }
      });
    }
  }, [messages]);

  // 履歴読み込み完了時に最下部にスクロール
  const prevHistoryLenRef = useRef(0);
  useEffect(() => {
    if (historySessions.length > 0 && prevHistoryLenRef.current === 0) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          setShowScrollButton(false);
        }
      });
    }
    prevHistoryLenRef.current = historySessions.length;
  }, [historySessions.length]);

  // メッセージ変更時にスクロール位置を再チェック（自動scrollToBottomはしない）
  useEffect(() => {
    checkScrollPosition();
  }, [messages, checkScrollPosition]);

  const scrollDown = useCallback(() => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientHeight * 0.7;
      scrollRef.current.scrollBy({ top: scrollAmount, behavior: "smooth" });
    }
  }, []);

  // TypingIndicatorは、AIメッセージがストリーミング中でない場合のみ表示
  const lastMessage = messages[messages.length - 1];
  const showTypingIndicator = isTyping && lastMessage?.role !== MessageRole.ASSISTANT;

  // 履歴セッションのメッセージをフラットに変換
  const historyMessages: Message[] = historySessions.flatMap((session) =>
    session.messages.map((msg) => ({
      id: msg.id,
      sessionId: session.id,
      role: msg.role === "user" ? MessageRole.USER : MessageRole.ASSISTANT,
      content: msg.content,
      images: msg.image_urls?.map((url) => ({ url })),
      createdAt: msg.created_at,
    })),
  );

  // 全メッセージ（履歴 + 現在のセッション）
  const allMessages = [...historyMessages, ...messages];

  return (
    <div
      ref={scrollRef}
      onScroll={checkScrollPosition}
      className="flex-1 overflow-y-auto p-6 flex-col-el gap-5 relative"
      style={{ scrollbarWidth: "thin", scrollbarColor: "#e0e4e7 transparent" }}
    >
      {/* ローディング表示 */}
      {(isLoadingHistory || isFetchingNextPage) && (
        <div className="flex-col-center py-4">
          <Spin />
        </div>
      )}

      {allMessages.map((message, index) => {
        const prevMessage = index > 0 ? allMessages[index - 1] : null;
        const isSessionChange =
          prevMessage && prevMessage.sessionId && message.sessionId !== prevMessage.sessionId;
        return (
          <Fragment key={message.id}>
            {isSessionChange && <SessionDivider type="start" timestamp={message.createdAt} />}
            <div
              className="flex-col-el shrink-0"
              ref={(el) => {
                if (message.role === MessageRole.USER) lastUserMsgRef.current = el;
                if (index === allMessages.length - 1) lastMsgRef.current = el;
              }}
              style={
                index === allMessages.length - 1 && lastMsgMinHeight > 0
                  ? { minHeight: lastMsgMinHeight }
                  : undefined
              }
            >
              <MessageBubble
                message={message}
                isStreaming={
                  isTyping &&
                  message.id === lastMessage?.id &&
                  message.role === MessageRole.ASSISTANT
                }
                userIcon={userIcon}
              />
            </div>
          </Fragment>
        );
      })}
      {showTypingIndicator && <TypingIndicator />}
      <div className="flex-row-el flex-center sticky w-full h-1 bottom-4">
        <ScrollToBottomButton visible={showScrollButton} onClick={scrollDown} />
      </div>
    </div>
  );
};
